import { create } from 'zustand'
import { SafetyConfig, PrivacySettings, SessionData } from '../types'
import { sessionManager } from '../services/dataRetention'

interface AppState {
  isLoading: boolean
  currentSession: SessionData | null
  safetyConfig: SafetyConfig
  privacySettings: PrivacySettings
  
  // Actions
  setLoading: (loading: boolean) => void
  initializeSession: () => void
  updateSession: (updates: Partial<SessionData>) => void
  clearSession: () => void
  updateSafetyConfig: (config: Partial<SafetyConfig>) => void
  updatePrivacySettings: (settings: Partial<PrivacySettings>) => void
  getSessionTimeRemaining: () => number
}

const defaultSafetyConfig: SafetyConfig = {
  moderationEnabled: true,
  offlineMode: false,
  strictMode: false
}

const defaultPrivacySettings: PrivacySettings = {
  dataRetentionHours: 24,
  autoCleanup: true,
  trackingEnabled: false
}

export const useAppStore = create<AppState>((set, get) => ({
  isLoading: false,
  currentSession: null,
  safetyConfig: defaultSafetyConfig,
  privacySettings: defaultPrivacySettings,

  setLoading: (loading: boolean) => set({ isLoading: loading }),

  initializeSession: () => {
    const existingSession = sessionManager.getCurrentSession()
    if (existingSession) {
      set({ currentSession: existingSession })
    } else {
      const newSession = sessionManager.createSession()
      set({ currentSession: newSession })
    }
  },

  updateSession: (updates: Partial<SessionData>) => {
    const current = get().currentSession
    if (!current) return

    const updatedSession = sessionManager.updateSession(updates)
    if (updatedSession) {
      set({ currentSession: updatedSession })
    }
  },

  clearSession: () => {
    sessionManager.clearSession()
    set({ currentSession: null })
  },

  updateSafetyConfig: (config: Partial<SafetyConfig>) => {
    set(state => ({
      safetyConfig: { ...state.safetyConfig, ...config }
    }))
  },

  updatePrivacySettings: (settings: Partial<PrivacySettings>) => {
    set(state => ({
      privacySettings: { ...state.privacySettings, ...settings }
    }))
  },

  getSessionTimeRemaining: () => {
    const sessionInfo = sessionManager.getSessionInfo()
    return sessionInfo.exists ? sessionInfo.timeToExpiry : 0
  }
}))